// We have to work with file indexing in the array to update the file object
/* eslint-disable eag/safe-object-access */
import {isFeatureEnabled} from 'feature-flags';
import {CustomRequestOption, FormControl, HelperText, Label, Upload} from 'platform/components';
import {Box, Hide, Show, useDevice} from 'platform/foundation';

import {useEffect, useState} from 'react';
import {useController, UseFormReturn} from 'react-hook-form';

import {
  useConvertPdfToImagesMutation,
  useLazyGetOriginalFileUrlQuery,
  useUploadFileMutation,
} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {StoragePdfConvertResponse, StorageUploadResponse} from '@prompt-bridge/shared/validations';

import {
  ApiError,
  getRandomId,
  isNotNilOrEmpty,
  JPEG_MIME_TYPE,
  PDF_MIME_TYPE,
  PNG_MIME_TYPE,
} from 'shared/web';

import {handleApiError} from '../../../utils/handleApiError';
import {EMPTY_FILE} from '../constants/EMPTY_FILE';
import {DocumentExtractionImportFile} from '../types/DocumentExtractionImportFile';
import {DocumentExtractionImportFormDefinition} from '../types/DocumentExtractionImportFormDefinition';
import {
  isStructuredXmlFile,
  normalizeDocumentUploadFile,
} from '../utils/normalizeDocumentUploadFile';
import {DocumentExtractionImportFileList} from './DocumentExtractionImportFileList';

interface DocumentExtractionImportFileFieldProps {
  name: keyof DocumentExtractionImportFormDefinition;
  control: FormControl<DocumentExtractionImportFormDefinition>;
  formApi: UseFormReturn<DocumentExtractionImportFormDefinition>;
}

const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;
const MAX_STRUCTURED_XML_BYTES = 8 * 1024 * 1024;

export function DocumentExtractionImportFileField(props: DocumentExtractionImportFileFieldProps) {
  const device = useDevice();

  const isMobile = device === 'mobile';

  const [filesToImport, setFilesToImport] = useState<DocumentExtractionImportFile[] | null>(null);

  const fileController = useController({
    name: props.name,
    control: props.control,
  });

  useEffect(
    function syncFilesToFormField() {
      fileController.field.onChange(filesToImport);
      // fileController.field is missing in dependencies because it causes an infinite loop
    },
    // eslint-disable-next-line react/exhaustive-deps
    [filesToImport]
  );

  // Any XML file is treated as a Nissan DIGLAS invoice, so XML upload stays behind a feature flag.
  const isNissanDiglasImportEnabled = isFeatureEnabled(
    featureFlags.AFS_WAREHOUSE_EXPERIMENTAL_EXTRACT_NISSAN_DIGLAS_INVOICE
  );
  const isStructuredInvoiceFile = (file: File) =>
    isNissanDiglasImportEnabled && isStructuredXmlFile(file);

  const [uploadFile] = useUploadFileMutation();
  const [getFileUrl] = useLazyGetOriginalFileUrlQuery();
  const [convertPdfToImages] = useConvertPdfToImagesMutation();

  const validateFileFormat = (file: File): boolean => {
    const allowedFormats = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    return isStructuredInvoiceFile(file)
      ? file.size <= MAX_STRUCTURED_XML_BYTES
      : allowedFormats.includes(file.type) && file.size <= MAX_DOCUMENT_BYTES;
  };

  const handlePdfConversion = async (fileId: string): Promise<string[]> => {
    const conversionResult: StoragePdfConvertResponse = await convertPdfToImages({
      fileId,
      namespace: 'prompt-bridge',
      generatorParams: {
        format: 'jpeg',
        density: 300,
        quality: 95,
        preserveAspectRatio: true,
        width: 1536,
      },
    }).unwrap();

    return conversionResult.accepted;
  };

  const handleUploadFile = async (options: CustomRequestOption) => {
    const file = isNissanDiglasImportEnabled
      ? normalizeDocumentUploadFile(options.file as File)
      : (options.file as File);
    const fileId = getRandomId();

    props.formApi.clearErrors(props.name);

    if (!validateFileFormat(file)) {
      return setFilesToImport((currentFiles) => [
        ...(currentFiles || []),
        {
          ...EMPTY_FILE,
          fileId,
          contentType: file.type,
          fileName: file.name,
          status: 'error',
          errorMessage: i18n.t('entity.documentExtraction.validations.invalidFileFormatOrSize'),
        } as DocumentExtractionImportFile,
      ]);
    }

    setFilesToImport((currentFiles) => [
      ...(currentFiles || []),
      {
        ...EMPTY_FILE,
        fileId,
        contentType: file.type,
        fileName: file.name,
        status: 'pending',
      } as DocumentExtractionImportFile,
    ]);

    try {
      const uploadResult: StorageUploadResponse = await uploadFile({
        file,
        namespace: 'prompt-bridge',
      }).unwrap();

      const fileUrl = await getFileUrl({
        fileId: uploadResult.fileId,
        namespace: 'prompt-bridge',
      }).unwrap();

      if (file.type === 'application/pdf') {
        const zipIds = await handlePdfConversion(uploadResult.fileId);

        return setFilesToImport((currentFiles) =>
          updateFileInArray(currentFiles, fileId, {
            zipIds,
            fileUri: fileUrl,
            status: 'success',
          })
        );
      }

      setFilesToImport((currentFiles) =>
        updateFileInArray(currentFiles, fileId, {
          storageServiceFileId: uploadResult.fileId,
          ...(isStructuredInvoiceFile(file) ? {sourceFormat: 'NISSAN_DIGLAS' as const} : {}),
          fileUri: fileUrl,
          status: 'success',
        })
      );
    } catch (error) {
      handleApiError(error as ApiError, {
        callback: () => {
          setFilesToImport((currentFiles) =>
            updateFileInArray(currentFiles, fileId, {
              status: 'error',
              errorMessage: i18n.t('entity.documentExtraction.validations.errorUploadingFile'),
            })
          );
        },
      });
    }
  };

  const handleDeleteFile = (fileId: string) => {
    setFilesToImport((currentFiles) => {
      if (!currentFiles) {
        return null;
      }

      const filteredFiles = currentFiles.filter((file) => file.fileId !== fileId);
      return filteredFiles.length > 0 ? filteredFiles : null;
    });
  };

  const updateFileInArray = (
    currentFiles: DocumentExtractionImportFile[] | null,
    fileId: string,
    updates: Partial<DocumentExtractionImportFile>
  ): DocumentExtractionImportFile[] => {
    const existingFileIndex = (currentFiles || []).findIndex((f) => f.fileId === fileId);

    if (existingFileIndex !== -1) {
      const updatedFiles = [...(currentFiles || [])];
      updatedFiles[existingFileIndex] = {
        ...updatedFiles[existingFileIndex],
        ...updates,
      };
      return updatedFiles;
    }

    return [
      ...(currentFiles || []),
      {
        ...EMPTY_FILE,
        fileId,
        ...updates,
      } as DocumentExtractionImportFile,
    ];
  };

  return (
    <>
      <Show when={isNotNilOrEmpty(filesToImport)}>
        <DocumentExtractionImportFileList
          name={props.name}
          control={props.control}
          filesToImport={filesToImport}
          validationMessage={fileController.fieldState.error?.message}
          onDelete={handleDeleteFile}
        />
      </Show>

      <Hide when={isNotNilOrEmpty(filesToImport)}>
        <Box width="100%">
          <Label isRequired>{i18n.t('entity.documentExtraction.actions.uploadDocument')}</Label>
          <Upload
            isMultiple
            type="card"
            accept={
              isNissanDiglasImportEnabled
                ? `${PDF_MIME_TYPE},${PNG_MIME_TYPE},${JPEG_MIME_TYPE},application/xml,text/xml,.xml`
                : `${PDF_MIME_TYPE},${PNG_MIME_TYPE},${JPEG_MIME_TYPE}`
            }
            size={isMobile ? 'default' : 'minHeight'}
            customRequest={handleUploadFile}
          />
          <HelperText errorMessage={fileController.fieldState.error?.message} />
        </Box>
      </Hide>
    </>
  );
}
